import React, { useEffect, useState } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';

import SplashScreen from "./screens/SplashScreen";
import Login from "./screens/Login";
import AssetsPreloader from "./AssetsPreLoader";
import LandScape from "./utils/landscape";
import OTPVerification from "./screens/OTPVerification";
import DesktopScreen from "./screens/DesktopScreen";
import MachineCodeScan from "./screens/MachineCodeScan";
import { RouteLoader } from "./screens/RouteLoader";
import Dashboard from "./screens/Dashboard";
import BiscuitScan from "./screens/BiscuitScan";
import Registeration from "./screens/Registeration";
import ShareVideo from "./screens/ShareVideo";
import WelcomeBack from "./screens/WelcomeBack";
import Reward from "./screens/Reward";
import Menu from "./screens/Menu";
import PageNotFound from "./screens/PageNotFound";

import { ProtectedRoute } from "./utils/protectRoute";
import TnCScreen from "./screens/TnCScreen";
import HowToPlay from "./screens/HowToPlay";
import PrivacyPolicy from "./screens/PrivacyPolicy";
import ContactUs from "./screens/ContactUs";
import AdminLogin from "./screens/admin/AdminLogin";
import CdpAdmin from "./screens/admin/CdpAdmin";
import { AdminProtectedRoute } from "./utils/protectAdminRoute";
import { gaEvent, trackMetaPageView } from "./analytics";
import EndedScreen from "./screens/EndedScreen";


// Improved device detection function
const isMobileDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const isMobileUserAgent =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent,
    );
  const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const isIOS = /iPhone|iPad|iPod/.test(userAgent);
  const isAndroid = /Android/.test(userAgent);
  if (isMobileUserAgent) {
    return true;
  }
  return hasTouch && (isIOS || isAndroid);
};

// Enhanced orientation detection
const isLandscapeMode = () => {
  if (window.screen.orientation) {
    const orientation = window.screen.orientation;
    return (
      orientation.type.includes("landscape") ||
      orientation.angle === 90 ||
      orientation.angle === 270
    );
  }
  if (typeof window.orientation !== "undefined") {
    return Math.abs(window.orientation) === 90;
  }
  if (window.matchMedia) {
    return window.matchMedia("(orientation: landscape)").matches;
  }
  return window.innerWidth > window.innerHeight;
};

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    gaEvent('page_view', { page_path: location.pathname + location.search });
    trackMetaPageView();
  }, [location]);
  return null;
}

function App() {
  const { t, i18n } = useTranslation();
  const language = localStorage.getItem("appLanguage") || "en";
  const [isMobile, setIsMobile] = useState(true);
  const [isLandscape, setIsLandscape] = useState(false);

  useEffect(() => {
    const checkDevice = () => setIsMobile(isMobileDevice());
    const checkOrientation = () => setIsLandscape(isLandscapeMode());

    checkDevice();
    checkOrientation();

    const handleResize = () => {
      checkDevice();
      checkOrientation();
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    if (window.screen.orientation) {
      window.screen.orientation.addEventListener("change", handleResize);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
      if (window.screen.orientation) {
        window.screen.orientation.removeEventListener("change", handleResize);
      }
    };
  }, []);

  if (!isMobile) {
    // Admin routes must be accessible from desktop too
    const adminPath = window.location.pathname;
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/cdp" element={<AdminProtectedRoute><CdpAdmin /></AdminProtectedRoute>} />
          {!adminPath.startsWith('/admin') && <Route path="*" element={<DesktopScreen />} />}
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <>
            <meta charSet="UTF-8" />
            <meta
                name="viewport"
                content="width=device-width, initial-scale=1, maximum-scale=1"
            />
            <link
                rel="shortcut icon"
                href="assets/images/favicon.ico"
                type="image/x-icon"
            />
            <link rel="stylesheet" href="assets/css/styles.css?ver=2.1" />
            <title>Thirukkural Challenge | Britannia</title>
            {/*=============== MOBILE SCREEN START ===============*/}
            <div className="challenge-ended-mobile">
                {/* ORANGE CORNERS */}
                <span className="challenge-ended-corner ce-tl" />
                <span className="challenge-ended-corner ce-tr" />
                <span className="challenge-ended-corner ce-bl" />
                <span className="challenge-ended-corner ce-br" />
                {/* FRAME */}
                <div className="challenge-ended-frame">
                    <div className="challenge-ended-canvas">
                        {/* RAYS BACKGROUND */}
                        <img
                            src="assets/images/end-star.png"
                            className="challenge-ended-rays"
                            alt=""
                        />
                        {/* CONTENT */}
                        <div className="challenge-ended-content">
                            {/* TOP SECTION */}
                            <div className="challenge-ended-header">
                                <div className="challenge-ended-brands">
                                    <img
                                        src="assets/images/animate-britania-logo.png"
                                        className="challenge-ended-britannia"
                                        alt="Britannia"
                                    />
                                    <img
                                        src="assets/images/milk-bikis-new-logo.png"
                                        className="challenge-ended-milk"
                                        alt="Milk Bikis"
                                    />
                                </div>
                                {/* THIRUKKURAL LOGO */}
                                <img
                                    src="assets/images/new-signup-logo.png"
                                    className="challenge-ended-logo"
                                    alt="Thirukkural Challenge"
                                />
                            </div>
                            {/* CENTER SECTION */}
                            <div className="challenge-ended-hero">
                                <div className="challenge-ended-headline-box">
                                    {/* CHALLENGE ENDED IMAGE */}
                                    <img
                                        src="assets/images/tamil.png"
                                        className="challenge-ended-headline-image"
                                        alt="The Thirukkural Challenge Has Ended"
                                    />
                                    {/* PEN */}
                                    <img
                                        src="assets/images/pen-end.png"
                                        className="challenge-ended-pen"
                                        alt=""
                                    />
                                </div>
                            </div>
                            {/* BOTTOM SECTION */}
                            <div className="challenge-ended-footer">
                                <p className="challenge-ended-follow">
                                    <strong>நெகஸ்ட் அப்டேட் <br />
                                    தெரிஞ்சுக்க மில்க் பிக்கிஸ் <br />
                                    பேஜ் ஃபாலோ பண்ணுங்க 
                                    </strong>
                                </p>
                                <div className="challenge-ended-socials">
                                    <a href="https://www.instagram.com/britanniamilkbikis" target='_blank' aria-label="Instagram">
                                        <img src="assets/images/insta-log.png" alt="Instagram" />
                                    </a>
                                    <a href="https://www.facebook.com/MilkBikis" target='_blank' aria-label="Facebook">
                                        <img src="assets/images/fb-logo.png" alt="Facebook" />
                                    </a>
                                </div>
                            </div>
                        </div>
                        {/* T&C */}
                        <div className="challenge-ended-legal">
                            <img src="assets/images/t-c-veg-log.png" alt="Vegetarian biscuits" />
                            <span>T&amp;C APPLY*</span>
                        </div>
                    </div>
                </div>
            </div>
            {/*=============== MOBILE SCREEN END ===============*/}
            {/*=============== DESKTOP SCREEN START ===============*/}
            <div className="d-frame-outer desktop-only">
                <img
                    src="assets/images/desktop-lower-star.png"
                    className="d-frame-star-bg"
                />
                {/* corners */}
                <span className="d-frame-corner tl" />
                <span className="d-frame-corner tr" />
                <span className="d-frame-corner bl" />
                <span className="d-frame-corner br" />
                <div className="d-frame-border-outer">
                    <div className="d-frame-border">
                        <div className="d-frame-border-inner">
                            <div className="d-frame-canvas">
                                {/* CONTENT */}
                                <div className="d-frame-content">
                                    <div className="desktop-center">
                                        <div className="desktop-content-box">
                                            {/* TOP LOGOS */}
                                            <div className="desktop-top-header">
                                                <img
                                                    src="assets/images/animate-britania-logo.png"
                                                    className="desktop-brand-logo"
                                                    alt="Britannia Logo"
                                                />
                                                <div className="desktop-title-wrap">
                                                    <img
                                                        src="assets/images/new-signup-logo.png"
                                                        className="desktop-main-title"
                                                        alt="Thirukkural Challenge"
                                                    />
                                                </div>
                                                <img
                                                    src="assets/images/milk-bikis-new-logo.png"
                                                    className="desktop-milk-logo"
                                                    alt="Milk Bikis Logo"
                                                />
                                            </div>
                                            {/* BOTTOM SECTION */}
                                            <div className="desktop-bottom">
                                                <h3 className="desktop-thank-text">
                                                    Thank you for your interest in participating!
                                                </h3>
                                                {/* QR BOX */}
                                                <div className="desktop-qr-box">
                                                    <span className="qr-corner qr-corner-tl" />
                                                    <span className="qr-corner qr-corner-tr" />
                                                    <span className="qr-corner qr-corner-bl" />
                                                    <span className="qr-corner qr-corner-br" />
                                                    <div className="qr-left">
                                                        <p className="qr-text">
                                                            Please scan the QR code on your phone to enter the
                                                            experience :
                                                        </p>
                                                    </div>
                                                    <div className="qr-right">
                                                        <img src="assets/images/desktop-qr.png" alt="QR Code" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/*=============== DESKTOP SCREEN END ===============*/}
        </>
  );
}

export default React.memo(App);
